import { Button } from "@msuite/ui/button";
import { useDisclosure, useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { PDFViewer } from "@msuite/ui/pdf-viewer";
import { Tooltip } from "@msuite/ui/tooltip";
import { responseHandler } from "@msuite/ui/utils";
import type { Nullable } from "@msuite/utils/types";
import { Fragment, useState } from "react";
import { client } from "@/hono";

/** Props Interface */
interface IncomingInvoiceDocumentPreviewButtonProps {
	fileId: string;
}

/**
 * Open an invoice's PDF in the viewer. The signed URL is fetched on the press rather than per
 * row: storage URLs expire, and a table would spend one on every invoice nobody opens.
 */
export function IncomingInvoiceDocumentPreviewButton({
	fileId,
}: IncomingInvoiceDocumentPreviewButtonProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const viewer = useDisclosure();
	const loader = useLoading();

	/** State */
	const [signedUrl, setSignedUrl] = useState<Nullable<string>>(null);

	/** Functions */
	async function handleOnView() {
		try {
			loader.start();
			const response = await client.files[":fileId"].$get({ param: { fileId } });
			const data = await responseHandler(response);
			if (!data.signedUrl) return;
			setSignedUrl(data.signedUrl);
			viewer.onOpen();
		} finally {
			loader.stop();
		}
	}

	/** Render */
	return (
		<Fragment>
			<Tooltip label={t("incoming_invoices.document")}>
				<Button
					size="xxs"
					variant="ghost"
					icon={<Icon name="Eye" />}
					onClick={handleOnView}
					isLoading={loader.isLoading}
					data-cy="incoming-invoice-document-preview-button"
				/>
			</Tooltip>
			{signedUrl && (
				<PDFViewer
					isOpen={viewer.isOpen}
					onClose={viewer.onClose}
					url={signedUrl}
				/>
			)}
		</Fragment>
	);
}
